import storefront from "../global/storefront";
import { PRODUCT_VARIANTS_PAGE_QUERY } from "../global/storefront/queries";

/*
 * Liquid's product.variants stops at 250, so a product with more (e.g. suit
 * pants: size × length × fit) renders a [data-section-data] variant list
 * that's missing everything past the 250th. This fetches the full list from
 * the Storefront API and maps each node into the same shape the Liquid JSON
 * uses, so product-form-selectors.js can merge the two without caring which
 * source a variant came from.
 */

const PAGE_SIZE = 250;
const variantCache = new Map();
const loadedVariants = new Map();

function numericId(gid) {
  return (
    Number(
      String(gid || "")
        .split("/")
        .pop(),
    ) || null
  );
}

function toCents(money) {
  if (!money?.amount) {
    return null;
  }

  return Math.round(Number(money.amount) * 100);
}

function richTextToPlainText(node) {
  if (!node) {
    return "";
  }

  if (node.type === "text") {
    return node.value || "";
  }

  const children = (node.children || []).map(richTextToPlainText);
  const separator = node.type === "root" || node.type === "list" ? "\n" : "";

  return children.join(separator);
}

function parseVariantNote(metafield) {
  if (!metafield?.value) {
    return "";
  }

  if (metafield.type === "rich_text_field") {
    try {
      return richTextToPlainText(JSON.parse(metafield.value)).trim();
    } catch (error) {
      return "";
    }
  }

  return String(metafield.value).trim();
}

/*
 * The Storefront API only exposes availableForSale (inventory quantities need
 * an extra token scope), so an available variant is mapped as untracked — the
 * same thing getVariantAvailability() already treats as always in stock —
 * and an unavailable one as tracked with nothing left.
 */
function mapVariant(node, optionNames, productUrl) {
  const id = numericId(node.id);
  const available = Boolean(node.availableForSale);

  const options = optionNames.map((optionName, index) => {
    const selectedOption = node.selectedOptions.find((option) => option.name === optionName);

    return {
      position: index + 1,
      name: selectedOption ? selectedOption.value : "",
    };
  });

  return {
    id,
    url: `${productUrl}?variant=${id}`,
    featuredMediaId: null,
    available,
    inventoryQuantity: available ? null : 0,
    inventoryPolicy: available ? "continue" : "deny",
    inventoryManagement: available ? null : "shopify",
    price: toCents(node.price),
    compareAtPrice: toCents(node.compareAtPrice),
    variantNote: parseVariantNote(node.variantNote),
    options,
  };
}

async function fetchAllVariants(productId, productUrl) {
  const variants = [];
  let after = null;
  let optionNames = null;

  do {
    const response = await storefront.request({
      query: PRODUCT_VARIANTS_PAGE_QUERY,
      variables: {
        id: `gid://shopify/Product/${productId}`,
        first: PAGE_SIZE,
        after,
      },
    });

    const product = response?.data?.product;

    if (!product) {
      break;
    }

    optionNames = optionNames || product.options.map((option) => option.name);

    product.variants.nodes.forEach((node) => {
      variants.push(mapVariant(node, optionNames, productUrl));
    });

    after = product.variants.pageInfo.hasNextPage ? product.variants.pageInfo.endCursor : null;
  } while (after);

  return variants;
}

export function loadAllVariants(productId, productUrl) {
  const cacheKey = String(productId);

  if (!variantCache.has(cacheKey)) {
    const request = fetchAllVariants(productId, productUrl)
      .then((variants) => {
        if (variants.length) {
          loadedVariants.set(cacheKey, variants);
        }

        return variants;
      })
      .catch((error) => {
        console.error("Unable to load the full variant list.", error);
        variantCache.delete(cacheKey);

        return [];
      });

    variantCache.set(cacheKey, request);
  }

  return variantCache.get(cacheKey);
}

/*
 * Synchronous read of an already-finished fetch, so a form re-rendered for a
 * product seen earlier this page view (variant URL change, ajax product swap)
 * can start from the full list instead of waiting a tick on the promise.
 */
export function getLoadedVariants(productId) {
  return loadedVariants.get(String(productId)) || null;
}
